import { useEffect, useMemo, useState } from 'react';
import { ClockRegular, LocationRegular, PeopleRegular } from '@fluentui/react-icons';
import {
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { MeetingInfo } from '../types/meeting';
import { IconField, SelectableTile, StickyActionFooter } from '../components';
import { useCateringService } from '../app/CateringProvider';
import { useOrderDraft } from '../state/OrderDraftProvider';
import { MAX_DELIVERY_WINDOWS } from '../services/cateringService';
import type { DeliveryWindow } from '../types/catering';
import { ORDER_PATHS, useRouter } from '../router';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minHeight: '100%',
    paddingBottom: '5.5rem',
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  choices: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    marginTop: 'auto',
  },
});

export type SetupDetailsPageProps = {
  meeting: MeetingInfo;
};

export default function SetupDetailsPage({ meeting }: SetupDetailsPageProps) {
  const styles = useStyles();
  const service = useCateringService();
  const { state, dispatch } = useOrderDraft();
  const { navigate } = useRouter();
  const [deliveryWindows, setDeliveryWindows] = useState<DeliveryWindow[]>([]);
  const [selectedWindowId, setSelectedWindowId] = useState(() => state.setup?.window.id ?? '');
  const room = meeting.location.displayName.trim();

  const [headcount, setHeadcount] = useState(
    state.setup?.headcount ?? Math.max(1, meeting.attendees.length + 1),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const meetingStart = meeting.start;
  const meetingEnd = meeting.end;
  const meetingTimeZone = meeting.timeZone;
  useEffect(() => {
    let isCurrent = true;

    if (!meetingStart || !meetingEnd) {
      setError('This meeting does not have a complete time range for delivery planning.');
      setIsLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    if (room.length === 0) {
      setError('A room is required to load catering options.');
      setIsLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setIsLoading(true);
    setError(null);
    Promise.all([
      service.getOutletContext(room),
      service.getDeliveryWindows({
        meetingStart,
        meetingEnd,
        timeZone: meetingTimeZone || undefined,
      }),
    ])
      .then(([, windows]) => {
        if (!isCurrent) return;
        setDeliveryWindows(windows.slice(0, MAX_DELIVERY_WINDOWS));
        setSelectedWindowId((current) => current || state.setup?.window.id || windows[0]?.id || '');
      })
      .catch(() => {
        if (!isCurrent) return;
        setError('We could not load delivery times. Please try again.');
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [service, room, meetingStart, meetingEnd, meetingTimeZone, state.setup?.window.id]);

  const selectedWindow = useMemo(
    () => deliveryWindows.find((window) => window.id === selectedWindowId),
    [deliveryWindows, selectedWindowId],
  );

  const validationMessage = !selectedWindow
    ? 'Choose a delivery time.'
    : room.trim().length === 0
      ? 'Enter a delivery room.'
      : !Number.isInteger(headcount) || headcount < 1
        ? 'Headcount must be a whole number of at least 1.'
        : null;

  const handleContinue = () => {
    if (!selectedWindow || validationMessage) return;

    dispatch({
      type: 'setSetup',
      setup: {
        window: selectedWindow,
        room: room.trim().slice(0, 50),
        headcount: Math.trunc(headcount),
      },
    });
    navigate(ORDER_PATHS.menu);
  };

  return (
    <main className={styles.root}>
      <div className={styles.intro}>
        <Text as="h1" size={500} weight="semibold">
          Setup Details
        </Text>
        <Text className={styles.hint}>Tell us a bit about your event.</Text>
      </div>

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <section className={styles.section} aria-labelledby="delivery-time-heading">
        <Text as="h2" size={300} weight="semibold" id="delivery-time-heading">
          Delivery Time
        </Text>
        {isLoading ? (
          <Spinner size="small" label="Loading delivery times" />
        ) : deliveryWindows.length === 0 ? (
          <Text className={styles.hint}>No delivery slots are available for this meeting.</Text>
        ) : (
          <div className={styles.choices}>
            {deliveryWindows.map((window) => (
              <SelectableTile
                key={window.id}
                icon={<ClockRegular aria-hidden="true" />}
                label={window.label}
                selected={window.id === selectedWindowId}
                onSelect={() => setSelectedWindowId(window.id)}
              />
            ))}
          </div>
        )}
      </section>

      <IconField
        icon={<LocationRegular aria-hidden="true" />}
        label="Room"
        value={room}
        readOnly
        disabled
      />
      <IconField
        icon={<PeopleRegular aria-hidden="true" />}
        label="Headcount"
        value={String(headcount)}
        type="number"
        min={1}
        suffix="people"
        onChange={(value) => setHeadcount(Number(value))}
        errorMessage={
          !Number.isInteger(headcount) || headcount < 1
            ? 'Enter a whole number of at least 1 person.'
            : undefined
        }
      />

      <div className={styles.footer}>
        <StickyActionFooter
          label="Continue"
          onClick={handleContinue}
          disabled={isLoading || Boolean(validationMessage)}
        />
      </div>
    </main>
  );
}
